/**
 * `wiring` — the one place the audio engine, the stores and Telegram meet.
 *
 * Everything below this file is deliberately ignorant of everything else: `engine/*`
 * owns one `HTMLAudioElement` and knows nothing about React or the catalogue;
 * `store/*` mirrors state and knows nothing about the element (`store/player`'s header
 * says so in as many words); `store/persist` is a sink that is handed a snapshot
 * getter. This module is the wire between them, and it is the only one.
 *
 * ## The three rules of §13.3, which this file exists to keep
 *
 * 1. **No `visibilitychange`-driven pause.** On Android, minimising the Mini App fires
 *    `visibilitychange` with `document.hidden === true` — the exact moment the user
 *    expects the music to keep playing. Telegram's own `activated` / `deactivated`
 *    (Bot API 8.0+) are used **only** for persistence and UI re-sync, and
 *    {@link createWiring}'s deactivate handler touches nothing on the element. That is
 *    asserted with spies in `states.test.tsx`, because it is the kind of regression
 *    that no test which does not dispatch the event will ever notice.
 * 2. **No `requestAnimationFrame` for progress or queue advance.** rAF stops firing on
 *    a hidden page, so a rAF-driven advance stalls the queue mid-session. Progress
 *    comes from `timeupdate` plus the engine's `setInterval`, and the queue advances
 *    from the `ended` event.
 * 3. **No autoplay before a gesture.** The first `play()` must happen synchronously
 *    inside a tap handler, which is why {@link Wiring.select} is not an `async
 *    function`: when the media URL is already minted — which is the normal case,
 *    because tokens are minted a track ahead (§9.5) — the element is pointed at it and
 *    played with **no `await` in front of the call**.
 *
 * ## What flows which way
 *
 * ```
 * engine events ──▶ player / queue store setters, Media Session, prefetch, recovery
 * store commands ◀── screens   ──▶ engine calls (src, play, pause, currentTime)
 * flush points  ──▶ persist.save({flush:true})   (pause · ended · deactivated)
 * ```
 *
 * `store/persist` owns the other three flush points itself (`visibilitychange` →
 * hidden, `pagehide`, and the 10 s interval while playing) through
 * `installFlushListeners`, so they are registered here and implemented there.
 */

import type { RepeatMode } from '@bl/shared';

import type { ApiClient } from '../lib/api';
import { api } from '../lib/api';
import type { TelegramFacade } from '../lib/telegram';
import { telegram as defaultTelegram } from '../lib/telegram';
import type { EngineEvent } from '../engine/events';
import { subscribeEngineEvents } from '../engine/events';
import type { MediaElementLike } from '../engine/element';
import { audio } from '../engine/element';
import { effectiveDuration, isFullySeekable } from '../engine/duration';
import { safePlay, unlockWith } from '../engine/unlock';
import type { MediaFailure, RecoveryController } from '../engine/recovery';
import { createRecovery } from '../engine/recovery';
import { isWithinPrefetchWindow, prefetchHead } from '../engine/prefetch';
import type { MediaSessionSetup } from '../engine/mediasession';
import {
  clearPositionState,
  setNowPlaying,
  setPlaybackState,
  setupMediaSession,
  syncPositionState,
} from '../engine/mediasession';
import type { PlayerErrorCode, PlayerState } from '../store/player';
import { usePlayerStore } from '../store/player';
import type { QueueState } from '../store/queue';
import {
  selectMediaTokenBatch,
  selectNextIndex,
  selectPreviousIndex,
  useQueueStore,
} from '../store/queue';
import type { Persistence, PersistenceSnapshot } from '../store/persist';
import { createPersistence } from '../store/persist';

/* ══ store surface ═════════════════════════════════════════════════════════════════ */

/**
 * The two methods this module needs from a zustand store.
 *
 * Structural on purpose: a `UseBoundStore` satisfies it, and so does a hand-written
 * double, which is what keeps the wiring testable without a store implementation.
 */
export interface StoreLike<T> {
  getState(): T;
  subscribe(listener: (state: T, previous: T) => void): () => void;
}

/* ══ dependencies ══════════════════════════════════════════════════════════════════ */

export interface WiringDeps {
  readonly el?: MediaElementLike;
  readonly player?: StoreLike<PlayerState>;
  readonly queue?: StoreLike<QueueState>;
  readonly client?: ApiClient;
  readonly telegram?: TelegramFacade;
  /** Injected by tests; created from the stores when absent. */
  readonly persistence?: Persistence;
  readonly recovery?: RecoveryController;
  /** `false` skips Media Session entirely — used by tests and by a headless boot. */
  readonly mediaSession?: boolean;
  /** Prefetch the head of the next track ~20 s out. `false` in tests. */
  readonly prefetch?: boolean;
}

export interface SelectOptions {
  /** Start playing once the source is set. Default `true`. */
  readonly autoplay?: boolean;
  /** Resume position in milliseconds, applied as the default playback start. */
  readonly startAtMs?: number;
}

export interface Wiring {
  /** Subscribes everything. Idempotent; returns the same `stop` every time. */
  start(): () => void;
  stop(): void;

  /* — commands the UI calls — */
  /** Play the item at `index` of the queue. Safe to call straight from a tap. */
  select(index: number, options?: SelectOptions): Promise<void>;
  play(): Promise<void>;
  pause(): void;
  toggle(): Promise<void>;
  next(): Promise<void>;
  previous(): Promise<void>;
  /** Absolute seek, in milliseconds. */
  seekTo(positionMs: number): void;
  /** Relative seek, in seconds — the ±5 s / ±30 s of the scrub bar's keyboard map. */
  seekBy(deltaS: number): void;
  /** Back to 0 and playing: the PULL UP release, and `repeat: one`. */
  restart(): Promise<void>;
  setVolume(volume: number): void;
  /**
   * Silence the deck without touching its level, and bring it back (§9.9).
   *
   * **This exists because `setVolume` does not work on iOS.** WebKit ignores writes to
   * `HTMLAudioElement.volume` outright — the property accepts the value and the output
   * does not change — so every duck built as a volume ramp is silently inert on every
   * iPhone, including the pull-up's `DUCK_VOLUME`. `muted` is honoured, which makes it
   * the only lever the PADS screen's `MUTE` cell can be built on.
   *
   * It is deliberately a hard mute rather than a ramp: a `muted` toggle has no curve to
   * ramp along, and riding the fader on a phone is a 100 ms gesture where the difference
   * is inaudible anyway. `setVolume` is left exactly as it is — the pull-up's duck still
   * wants a level on the platforms that honour one.
   */
  setMuted(muted: boolean): void;
  setRepeatMode(mode: RepeatMode): void;

  /** The persistence sink, so the entry point can `load()` on boot. */
  readonly persistence: Persistence;
  /** The Media Session registration report, for diagnostics. `null` before `start`. */
  readonly mediaSession: MediaSessionSetup | null;
}

/* ══ failure mapping ═══════════════════════════════════════════════════════════════ */

/** `engine/recovery`'s classification → the store's renderable error code. */
export function toPlayerErrorCode(failure: MediaFailure): PlayerErrorCode | null {
  switch (failure) {
    case 'network':
      return 'MEDIA_NETWORK';
    case 'decode':
      return 'MEDIA_DECODE';
    case 'unsupported':
      return 'MEDIA_SRC';
    case 'unknown':
      return 'MEDIA_UNKNOWN';
    case 'aborted':
      /* §13.6: ignore. It means the user did something — a skip, a new `src` — and
         painting an error plate over a deliberate action is a lie. */
      return null;
    default:
      return 'MEDIA_UNKNOWN';
  }
}

/* ══ the factory ═══════════════════════════════════════════════════════════════════ */

export function createWiring(deps: WiringDeps = {}): Wiring {
  const el = deps.el ?? audio;
  const player = deps.player ?? usePlayerStore;
  const queue = deps.queue ?? useQueueStore;
  const client = deps.client ?? api;
  const tg = deps.telegram ?? defaultTelegram;
  const wantMediaSession = deps.mediaSession !== false;
  const wantPrefetch = deps.prefetch !== false;

  /** The live snapshot `store/persist` asks for at the instant of every write. */
  const getSnapshot = (): PersistenceSnapshot => {
    const p = player.getState();
    const q = queue.getState();
    return {
      trackId: p.trackId,
      positionMs: p.positionMs,
      queueTrackIds: q.items.map((item) => item.track.id),
      queueIndex: q.index,
      shuffle: q.shuffle,
      shuffleSeed: q.shuffleSeed,
      repeatMode: q.repeatMode,
      volume: p.volume,
      isPlaying: p.isPlaying,
    };
  };

  const persistence = deps.persistence ?? createPersistence({ getSnapshot, client });

  /* — small helpers ─────────────────────────────────────────────────────────────── */

  const currentTrackId = (): number | null => {
    const q = queue.getState();
    return q.items[q.index]?.track.id ?? null;
  };

  const serverDurationS = (): number => player.getState().serverDurationS;

  const flush = (): void => {
    void persistence.save({ flush: true });
  };

  /* — recovery ──────────────────────────────────────────────────────────────────── */

  const recovery =
    deps.recovery ??
    createRecovery({
      el,
      /* One re-mint per track, for the expired media token behind a latched
         MEDIA_ERR_SRC_NOT_SUPPORTED (§13.6). */
      refreshSrc: async (): Promise<string | null> => {
        const trackId = currentTrackId();
        if (trackId === null) return null;
        client.forgetMediaUrls(trackId);
        const urls = await client.ensureMediaUrls([trackId]);
        return urls[trackId] ?? null;
      },
      onAttempt: (attempt) => {
        player.getState().noteRecoveryAttempt();
        const code = toPlayerErrorCode(attempt.failure);
        if (code !== null) player.getState().setError(code);
      },
      onStall: () => {
        player.getState().setBuffering(true);
        player.getState().setError('MEDIA_STALLED');
      },
      onGiveUp: (info) => {
        const code = toPlayerErrorCode(info.failure);
        if (code !== null) player.getState().setError(code);
        /* §13.6: a source the element cannot play, after one re-mint, is marked and
           skipped rather than retried into the ground. */
        if (info.failure === 'unsupported') void next();
      },
    });

  /* — media URLs ────────────────────────────────────────────────────────────────── */

  /**
   * The URL for a track if it is already minted, else `null`.
   *
   * Never issues a request, because the whole point of the batched token of §9.5 is
   * that the answer is already here when the tap arrives.
   */
  const cachedUrl = (trackId: number): string | null => client.mediaUrl(trackId);

  /** Mints the `[current, next]` pair. Failures are the recovery ladder's problem. */
  const warmTokens = async (): Promise<void> => {
    const ids = selectMediaTokenBatch(queue.getState());
    if (ids.length === 0) return;
    try {
      await client.ensureMediaUrls(ids);
    } catch {
      /* Left to the element: a missing URL surfaces as a media error, which is the
         one path that already has a backoff ladder and a UI. */
    }
  };

  /* — prefetch ──────────────────────────────────────────────────────────────────── */

  let prefetchedFor: number | null = null;

  const maybePrefetch = (remainingS: number): void => {
    if (!wantPrefetch) return;
    if (!isWithinPrefetchWindow(remainingS)) return;

    const q = queue.getState();
    const nextIndex = selectNextIndex(q);
    if (nextIndex < 0 || nextIndex === q.index) return;
    const track = q.items[nextIndex]?.track;
    if (track === undefined || prefetchedFor === track.id) return;

    const url = cachedUrl(track.id);
    if (url === null) return;
    prefetchedFor = track.id;
    void prefetchHead(url).promise;
  };

  /* — playback ──────────────────────────────────────────────────────────────────── */

  const needGesture = (): void => {
    player.getState().setNeedsGesture(true);
    player.getState().setError('MEDIA_NEEDS_GESTURE');
  };

  /**
   * Points the element at `src` and starts it.
   *
   * **Order is load-bearing and there is no `await` before the play call.** Before the
   * element has ever been unlocked this goes through `unlockWith`, which writes
   * `currentTime` as the *default playback start position* while `readyState` is
   * `HAVE_NOTHING` — the only way to resume mid-track with no visible jump from 0 and
   * no listener outside the gesture (§13.2).
   */
  const startSource = (src: string, startAtMs: number, autoplay: boolean): Promise<void> => {
    if (!autoplay) {
      el.src = src;
      if (startAtMs > 0) el.currentTime = startAtMs / 1000;
      return Promise.resolve();
    }

    if (!player.getState().unlocked) {
      return unlockWith(src, startAtMs, el).then((outcome) => {
        if (outcome.ok) {
          player.getState().setUnlocked(true);
          return;
        }
        if (outcome.reason === 'gesture-required') needGesture();
      });
    }

    el.src = src;
    if (startAtMs > 0) el.currentTime = startAtMs / 1000;
    return safePlay(needGesture, el).then(() => undefined);
  };

  /**
   * Select the queue item at `index` and (by default) play it.
   *
   * Not an `async function`: an `async` prologue still runs synchronously, but the
   * guarantee would then rest on nobody ever inserting an `await` above the play call.
   * A plain function returning a promise makes the invariant structural — the same
   * reasoning `engine/unlock.ts` gives for `unlockWith`.
   */
  const select = (index: number, options: SelectOptions = {}): Promise<void> => {
    const q = queue.getState();
    const item = q.items[index];
    if (item === undefined) return Promise.resolve();

    const autoplay = options.autoplay !== false;
    const startAtMs = options.startAtMs ?? 0;

    q.setIndex(index);
    player.getState().setTrack(item.track.id, item.track.durationS);
    recovery.reset();
    prefetchedFor = null;
    /* Without this the OS keeps the previous track's duration and a playhead that
       appears to jump backwards (§13.4). */
    if (wantMediaSession) {
      clearPositionState();
      setNowPlaying({ title: item.track.title, artist: item.track.performer ?? '' });
    }

    const url = cachedUrl(item.track.id);
    if (url !== null) {
      const started = startSource(url, startAtMs, autoplay);
      /* Warming the next token is fire-and-forget and must not delay the tap. */
      void warmTokens();
      return started;
    }

    /* No token yet: this cannot be the gesture-bound path, so the element may end up
       needing a tap. That is reported, not hidden. */
    return client
      .ensureMediaUrls(selectMediaTokenBatch(queue.getState()))
      .then((urls) => {
        const fresh = urls[item.track.id];
        if (fresh === undefined) {
          player.getState().setError('MEDIA_SRC');
          return;
        }
        return startSource(fresh, startAtMs, autoplay);
      })
      .catch(() => {
        player.getState().setError('MEDIA_NETWORK');
      });
  };

  const play = async (): Promise<void> => {
    const q = queue.getState();
    if (q.index < 0 && q.items.length > 0) {
      await select(0);
      return;
    }
    if (el.src === '') {
      await select(q.index < 0 ? 0 : q.index);
      return;
    }
    await safePlay(needGesture, el);
  };

  const pause = (): void => {
    el.pause();
  };

  const toggle = async (): Promise<void> => {
    if (el.paused) {
      await play();
      return;
    }
    pause();
  };

  const next = async (): Promise<void> => {
    const index = selectNextIndex(queue.getState());
    if (index < 0) {
      pause();
      return;
    }
    await select(index);
  };

  const previous = async (): Promise<void> => {
    /* §9.4's convention: past three seconds, "previous" means "start this one again". */
    if (el.currentTime > 3) {
      seekTo(0);
      return;
    }
    const index = selectPreviousIndex(queue.getState());
    if (index < 0) {
      seekTo(0);
      return;
    }
    await select(index);
  };

  const seekTo = (positionMs: number): void => {
    if (!Number.isFinite(positionMs)) return;
    const duration = effectiveDuration(serverDurationS(), el);
    const seconds = Math.max(0, positionMs / 1000);
    el.currentTime = duration > 0 ? Math.min(seconds, duration) : seconds;
    player.getState().setPosition(el.currentTime * 1000);
  };

  const seekBy = (deltaS: number): void => {
    if (!Number.isFinite(deltaS)) return;
    seekTo((el.currentTime + deltaS) * 1000);
  };

  const restart = async (): Promise<void> => {
    seekTo(0);
    if (el.paused) await play();
  };

  const setVolume = (volume: number): void => {
    if (!Number.isFinite(volume)) return;
    const clamped = Math.min(1, Math.max(0, volume));
    /* `audio.volume` is the whole ducking mechanism: there is no Web Audio graph in
       this app, because Telegram's audio paths frequently deny one (§7.3, §9.6). */
    const sink = el as MediaElementLike & { volume?: number };
    if (typeof sink.volume === 'number') sink.volume = clamped;
    player.getState().setVolume(clamped);
  };

  const setMuted = (muted: boolean): void => {
    /* Not routed through the player store: `muted` is a momentary transport state that
       nothing persists and nothing else reads, unlike `volume`, which the resume banner
       restores. Adding it to the store would mean a field every writer has to remember
       to clear. */
    const sink = el as MediaElementLike & { muted?: boolean };
    if (typeof sink.muted === 'boolean') sink.muted = muted;
  };

  const setRepeatMode = (mode: RepeatMode): void => {
    queue.getState().setRepeatMode(mode);
    flush();
  };

  /* — engine → store ────────────────────────────────────────────────────────────── */

  const onEngineEvent = (event: EngineEvent): void => {
    const { snapshot } = event;
    const p = player.getState();

    switch (event.kind) {
      case 'metadata':
      case 'duration':
        p.setDuration(snapshot.durationS * 1000);
        p.setFullySeekable(isFullySeekable(serverDurationS(), el));
        if (wantMediaSession) syncPositionState(serverDurationS(), { el });
        break;

      case 'time':
        p.setPosition(snapshot.positionS * 1000);
        p.setBufferedAhead(snapshot.bufferedAheadS);
        if (p.isBuffering) p.setBuffering(false);
        maybePrefetch(Math.max(0, effectiveDuration(serverDurationS(), el) - snapshot.positionS));
        break;

      case 'progress':
        p.setBufferedAhead(snapshot.bufferedAheadS);
        break;

      case 'waiting':
      case 'stalled':
        p.setBuffering(true);
        break;

      case 'suspend':
        /* The NORMAL end of buffering, never an error (§13.6). */
        p.setBuffering(false);
        break;

      case 'playing':
        p.setPlaying(true);
        p.setBuffering(false);
        p.setError(null);
        if (wantMediaSession) {
          setPlaybackState('playing');
          syncPositionState(serverDurationS(), { el });
        }
        break;

      case 'pause':
        p.setPlaying(false);
        if (wantMediaSession) {
          setPlaybackState('paused');
          syncPositionState(serverDurationS(), { el });
        }
        flush();
        break;

      case 'ended': {
        p.setPlaying(false);
        flush();
        const q = queue.getState();
        const item = q.items[q.index];
        if (item !== undefined) q.markPlayed(item.id);
        if (q.repeatMode === 'one') {
          void restart();
          break;
        }
        /* The `ended` event advances the queue — not a timer, and never rAF, which
           does not fire while the app is minimised (§13.3). */
        void next();
        break;
      }

      case 'error':
        /* `engine/recovery` owns the backoff ladder; the store only shows what it
           reports through `onAttempt` / `onGiveUp`. */
        p.setPlaying(false);
        break;

      case 'seeked':
        p.setPosition(snapshot.positionS * 1000);
        p.setSeeking(false);
        if (wantMediaSession) syncPositionState(serverDurationS(), { el });
        break;

      case 'ratechange':
        p.setPlaybackRate(snapshot.playbackRate);
        if (wantMediaSession) syncPositionState(serverDurationS(), { el });
        break;

      default:
        break;
    }
  };

  /* — lifecycle ─────────────────────────────────────────────────────────────────── */

  let stopEngine: (() => void) | null = null;
  let stopFlush: (() => void) | null = null;
  let offActivated: (() => void) | null = null;
  let offDeactivated: (() => void) | null = null;
  let session: MediaSessionSetup | null = null;
  let started = false;

  const stop = (): void => {
    if (!started) return;
    started = false;
    stopEngine?.();
    stopFlush?.();
    offActivated?.();
    offDeactivated?.();
    session?.teardown();
    recovery.stop();
    stopEngine = null;
    stopFlush = null;
    offActivated = null;
    offDeactivated = null;
    session = null;
  };

  const start = (): (() => void) => {
    if (started) return stop;
    started = true;

    stopEngine = subscribeEngineEvents(onEngineEvent, { el });
    recovery.start();
    stopFlush = persistence.installFlushListeners();

    if (wantMediaSession) {
      session = setupMediaSession(
        {
          play: () => {
            void play();
          },
          pause,
          stop: () => {
            pause();
            seekTo(0);
          },
          previoustrack: () => {
            void previous();
          },
          nexttrack: () => {
            void next();
          },
          seekbackward: (offsetS) => {
            seekBy(-offsetS);
          },
          seekforward: (offsetS) => {
            seekBy(offsetS);
          },
          seekto: (timeS) => {
            seekTo(timeS * 1000);
          },
        },
        { el },
      );
    }

    /*
     * Telegram 8.0+ `activated` / `deactivated`.
     *
     * THE MOST IMPORTANT FOUR LINES IN THIS FILE. `deactivated` fires when the user
     * minimises the Mini App — which on Android is the same moment `visibilitychange`
     * fires — and it must persist and nothing else. It does not pause, it does not
     * touch `src`, it does not read `currentTime` off the element to "help". Music
     * playing while Telegram is minimised is the product (§13.3, §16.2).
     */
    offDeactivated = tg.onDeactivated(() => {
      flush();
    });

    /* Coming back: re-sync the UI from what the element actually did while we were
       not looking. Reads only — the element is never commanded here. */
    offActivated = tg.onActivated(() => {
      const p = player.getState();
      p.setPlaying(!el.paused);
      p.setPosition(el.currentTime * 1000);
      p.setDuration(el.duration * 1000);
      p.setFullySeekable(isFullySeekable(serverDurationS(), el));
    });

    return stop;
  };

  return {
    start,
    stop,
    select,
    play,
    pause,
    toggle,
    next,
    previous,
    seekTo,
    seekBy,
    restart,
    setVolume,
    setMuted,
    setRepeatMode,
    persistence,
    get mediaSession(): MediaSessionSetup | null {
      return session;
    },
  };
}

/* ══ the app-wide instance ═════════════════════════════════════════════════════════ */

let instance: Wiring | null = null;

/**
 * The process-wide wiring, created on first use.
 *
 * Lazy, so importing this module has no side effects and subscribes to nothing: the
 * entry point calls `getWiring().start()` once, after the shell has rendered and
 * `telegram.ready()` has hidden the splash.
 */
export function getWiring(): Wiring {
  instance ??= createWiring();
  return instance;
}

/** Drops the instance. For HMR and for tests. */
export function resetWiring(): void {
  instance?.stop();
  instance = null;
}
